import { Company } from 'module/api/company/types';
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { CompanyAPI } from 'api/company';
import {
  KanbanBoardResponse,
  KanbanCountResponse,
  KanbanPaginationParams,
  SignalOccurrence,
  SignalOccurrenceParams,
} from 'api/company/types';

const getCompanyByIdKey = (companyId: number) => ['company', companyId];

export const useGetCompanyById = (
  companyId: number,
  queryOptions?: Omit<
    UseQueryOptions<
      Company,
      Error,
      Company,
      ReturnType<typeof getCompanyByIdKey>
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: getCompanyByIdKey(companyId),
    queryFn: () => CompanyAPI.getCompanyById(companyId),
    ...queryOptions,
  });
};

export const useGetAllCustomerCompanies = (
  queryOptions?: Omit<
    UseQueryOptions<
      Company[],
      Error,
      Company[],
      ReturnType<typeof getCompanyByIdKey>
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: ['companies', 'customer'],
    queryFn: () => CompanyAPI.getCustomerCompanies(),
    ...queryOptions,
  });
};

export const useKanbanCountQuery = (): UseQueryResult<
  KanbanCountResponse,
  Error
> => {
  return useQuery({
    queryKey: ['kanban-count'],
    queryFn: () => CompanyAPI.getKanbanCount(),
    staleTime: 1000 * 60 * 5,
  });
};

export const useAccountSignalBoardQuery = (
  params: KanbanPaginationParams,
): UseQueryResult<KanbanBoardResponse, Error> => {
  return useQuery({
    queryKey: ['signal-board', params],
    queryFn: () => CompanyAPI.getAccountSignalBoard(params),
    refetchOnMount: false,
    staleTime: 1000 * 60 * 5,
  });
};

export const useSignalOccurrenceQuery = (
  params: SignalOccurrenceParams,
): UseQueryResult<SignalOccurrence[], Error> => {
  const { companyId, configId, signalOccurrenceId, enabled = false } = params;

  return useQuery({
    queryKey: ['signal-occurrence', companyId, configId, signalOccurrenceId],
    queryFn: () => CompanyAPI.getSignalOccurrence(params),
    enabled,
  });
};
